import React from "react";
import { IVariableBasePrices, IVariableBasePriceOverrides } from "@llws/lift-entity-interfaces";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField,
} from "@mui/material";
import { floatVal, Nullable } from "@jamesgmarks/utilities";
import { StyledTableCell, StyledTableRow } from "src/components/parts/mui/StyledTables";
import { StyledTextField } from "src/components/pages/Subscriptions/tiered-unit-count/styledComponents";
import { roundFloatToCents } from "@hydra/internal";

interface IVariableBasePriceWithRange extends Partial<IVariableBasePrices> {
  range: string;
}

export interface IVariableBasePriceOverrideWithRange extends Partial<IVariableBasePriceOverrides> {
  range: string;
}

interface VariableBasePriceTableProps {
  rows: (IVariableBasePriceWithRange | IVariableBasePriceOverrideWithRange)[];
  onChange: Nullable<(index: number, key: keyof IVariableBasePriceOverrideWithRange, value: number | null) => void>;
  readOnly?: boolean;
}

export const VariableBasePriceTable: React.FC<VariableBasePriceTableProps> = ({
  rows,
  onChange = null,
  readOnly = false,
}) => {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <StyledTableRow>
            <StyledTableCell><b>Range</b></StyledTableCell>
            {!readOnly && <StyledTableCell><b>Max Value</b></StyledTableCell>}
            <StyledTableCell><b>Base Price</b></StyledTableCell>
            <StyledTableCell><b>Cost Per Unit</b></StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <StyledTableRow key={i} dark={true} >
              <StyledTableCell dark={true}>{row.range}</StyledTableCell>
              {
                !readOnly
                && <StyledTableCell dark={true}>
                  <StyledTextField
                    type="number"
                    size="small"
                    fullWidth
                    value={row.maxValue ?? ""}
                    onChange={(e) => {
                      if (onChange) {
                        onChange(
                          i,
                          "maxValue",
                          (
                            e.target.value.trim() === "" || Number.isNaN(Number(e.target.value))
                              ? null
                              : Number(e.target.value)
                          ),
                        );
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                        color: 'black',
                      },
                      '& input': {
                        color: 'black',
                      },
                    }}
                  />
                </StyledTableCell>
              }
              <StyledTableCell dark={true}>
                {readOnly ? (
                  row.basePrice != null ? roundFloatToCents(row.basePrice) : "N/A"
                ) : (
                  <StyledTextField
                    type="number"
                    size="small"
                    fullWidth
                    value={row.basePrice ? roundFloatToCents(row.basePrice) : ""}
                    onChange={(e) => {
                      if (onChange) {
                        onChange(
                          i,
                          "basePrice",
                          roundFloatToCents(
                            Math.max(0, Number(e.target.value)),
                          ),
                        );
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                        color: 'black',
                      },
                      '& input': {
                        color: 'black',
                      },
                    }}
                  />
                )}
              </StyledTableCell>
              <StyledTableCell dark={true}>
                {readOnly ? (
                  row.costPerUnit != null ? roundFloatToCents(row.costPerUnit) : "N/A"
                ) : (
                  <StyledTextField
                    type="number"
                    size="small"
                    fullWidth
                    value={row.costPerUnit ? roundFloatToCents(row.costPerUnit) : 0}
                    onChange={(e) => {
                      if (onChange) {
                        onChange(
                          i,
                          "costPerUnit",
                          (
                            e.target.value.trim() === "" || Number.isNaN(Number(e.target.value))
                              ? 0
                              : roundFloatToCents(Number(e.target.value))
                          ),
                        );
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: 'white',
                        color: 'black',
                      },
                      '& input': {
                        color: 'black',
                      },
                    }}
                  />
                )}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
